/**
 * DecisionFlow — MarginQuotaManager
 * ui/src/components/tenant-admin/MarginQuotaManager.jsx
 *
 * Domain: §P4-03 Margin Quota
 * Embedded in: PlanDetailView modifier card grid (Pattern A)
 * API:
 *   GET  /api/v1/margin-quotas?plan_component_id={id}
 *   POST /api/v1/margin-quotas
 *   POST /api/v1/margin-quotas/{id}/approve
 * Feature gate: Enterprise — margin_compensation
 *   403 FEATURE_UNAVAILABLE → GateStatusBanner, no list rendered.
 * tenant_super_admin excluded (NN-36).
 * State derived from approved_by / submitted_at — no status column.
 * planComponentId prop is required — all API calls scoped to it.
 * Path: ui/src/components/tenant-admin/MarginQuotaManager.jsx
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useFeatures } from '../../auth/FeatureContext.jsx';
import { apiGet, apiPost } from '../../api/client';
import ApprovalPanel from '../shared/ApprovalPanel.jsx';
import GateStatusBanner from '../shared/GateStatusBanner.jsx';
import StatusPill from '../shared/StatusPill.jsx';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deriveState(entity) {
  if (entity.approved_by)                         return 'approved';
  if (entity.submitted_at || entity.submitted_by) return 'pending_approval';
  return 'draft';
}

function getApiErrorCode(error) { return error?.code || null; }
function getErrorMessage(error, fallback) { return error?.message || fallback; }

// ---------------------------------------------------------------------------
// CSS tokens
// ---------------------------------------------------------------------------

const thClass      = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]';
const tdClass      = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]';
const btnPrimary   = 'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';
const btnSecondary = 'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';
const inputClass   = 'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]';
const labelClass   = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

// ---------------------------------------------------------------------------
// Form defaults
// ---------------------------------------------------------------------------

const EMPTY_FORM = {
  margin_threshold_pct: '',
  payout_rate_pct:      '',
  effective_date:       '',
  expiration_date:      '',
  notes:                '',
};

// ---------------------------------------------------------------------------
// MarginQuotaManager
// ---------------------------------------------------------------------------

export default function MarginQuotaManager({
  mode, period, wizardSteps, embedded = false,
  planId, planComponentId, fiscalPeriodId, readOnly = false,
}) {
  const { user } = useAuth();
  const { hasPlanType } = useFeatures();

  if (!hasPlanType('feature.plan.margin')) {
    return <GateStatusBanner code="PLAN_TYPE_NOT_ENTITLED" />;
  }

  const [gated, setGated]                   = useState(false);
  const [items, setItems]                   = useState([]);
  const [listLoading, setListLoading]       = useState(false);
  const [listError, setListError]           = useState('');
  const [selectedItem, setSelectedItem]     = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [form, setForm]                     = useState(EMPTY_FORM);
  const [saving, setSaving]                 = useState(false);
  const [formError, setFormError]           = useState('');
  const [actionLoading, setActionLoading]   = useState(false);
  const [actionError, setActionError]       = useState('');
  const [toast, setToast]                   = useState(null);

  const showToast = useCallback((msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  // ---------------------------------------------------------------------------
  // Load — enterprise gate check first
  // ---------------------------------------------------------------------------

  const loadItems = useCallback(async () => {
    if (!user || !planComponentId) return;
    setListLoading(true);
    setListError('');
    setGated(false);
    try {
      const data = await apiGet(`/api/v1/margin-quotas?plan_component_id=${planComponentId}`);
      setItems(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'FEATURE_UNAVAILABLE') {
        setGated(true);
      } else {
        setListError(getErrorMessage(error, 'Unable to load margin quotas.'));
      }
    } finally {
      setListLoading(false);
    }
  }, [user, planComponentId]);

  useEffect(() => { loadItems(); }, [loadItems]);

  // ---------------------------------------------------------------------------
  // Create
  // ---------------------------------------------------------------------------

  async function handleCreate(e) {
    e.preventDefault();
    if (!form.margin_threshold_pct || !form.payout_rate_pct) {
      setFormError('Margin threshold and payout rate are required.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      await apiPost('/api/v1/margin-quotas', {
        plan_component_id:    planComponentId,
        margin_threshold_pct: parseFloat(form.margin_threshold_pct),
        payout_rate_pct:      parseFloat(form.payout_rate_pct),
        effective_date:       form.effective_date || null,
        expiration_date:      form.expiration_date || null,
        notes:                form.notes.trim() || null,
      });
      setShowCreateForm(false);
      setForm(EMPTY_FORM);
      showToast('Margin quota created.');
      await loadItems();
    } catch (error) {
      setFormError(getErrorMessage(error, 'Failed to create margin quota.'));
    } finally {
      setSaving(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  async function handleSubmit() {
    if (!selectedItem) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/margin-quotas/${selectedItem.margin_quota_id}/submit`);
      showToast('Submitted for approval.');
      await loadItems();
      setSelectedItem((prev) => ({ ...prev, submitted_at: new Date().toISOString() }));
    } catch (error) {
      setActionError(getErrorMessage(error, 'Failed to submit for approval.'));
    } finally {
      setActionLoading(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Approve
  // ---------------------------------------------------------------------------

  async function handleApprove() {
    if (!selectedItem) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/margin-quotas/${selectedItem.margin_quota_id}/approve`);
      showToast('Margin quota approved.');
      await loadItems();
      setSelectedItem((prev) => ({ ...prev, approved_by: user?.user_id || user?.id }));
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'SELF_APPROVAL_NOT_ALLOWED') {
        setActionError('Self-approval is not permitted (NN-10).');
      } else {
        setActionError(getErrorMessage(error, 'Failed to approve.'));
      }
    } finally {
      setActionLoading(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Guards
  // ---------------------------------------------------------------------------

  const currentUserId = user?.user_id || user?.id || null;

  if (!user) {
    return (
      <section className="flex flex-col gap-4">
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          Authentication required.
        </div>
      </section>
    );
  }

  // Enterprise gate
  if (gated) {
    return (
      <section className="flex flex-col gap-4">
        <GateStatusBanner feature="margin_compensation" />
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Detail view
  // ---------------------------------------------------------------------------

  if (selectedItem) {
    const state = deriveState(selectedItem);
    return (
      <section className="flex flex-col gap-4">
        <button type="button" onClick={() => { setSelectedItem(null); setActionError(''); }} className={btnSecondary + ' w-fit'}>
          ← Back to list
        </button>

        <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">
              Margin Quota — {selectedItem.margin_threshold_pct}% threshold
            </h2>
            <StatusPill status={state} />
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Margin Threshold</p>
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                {selectedItem.margin_threshold_pct != null ? `${selectedItem.margin_threshold_pct}%` : '—'}
              </p>
            </div>
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Payout Rate</p>
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                {selectedItem.payout_rate_pct != null ? `${selectedItem.payout_rate_pct}%` : '—'}
              </p>
            </div>
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Effective</p>
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                {selectedItem.effective_date ?? '—'}
              </p>
            </div>
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Expires</p>
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                {selectedItem.expiration_date ?? '—'}
              </p>
            </div>
            {selectedItem.notes && (
              <div className="col-span-2 sm:col-span-3">
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Notes</p>
                <p className="text-tok-sm text-[rgb(var(--color-text))]">{selectedItem.notes}</p>
              </div>
            )}
          </div>

          {actionError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
              {actionError}
            </div>
          )}
        </div>

        {!readOnly && (
          <ApprovalPanel
            variant="margin_quota"
            currentState={state}
            submittedBy={selectedItem.submitted_by}
            currentUserId={currentUserId}
            onSubmit={state === 'draft' ? handleSubmit : null}
            onApprove={state === 'pending_approval' ? handleApprove : null}
            onReject={null}
            approvedBy={selectedItem.approved_by}
            approvedAt={selectedItem.approved_at}
            loading={actionLoading}
          />
        )}
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // List view
  // ---------------------------------------------------------------------------

  return (
    <section className="flex flex-col gap-4">

      {readOnly && (
        <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Plan is approved — configuration is read-only.
        </div>
      )}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Margin Quotas</h2>
        {!readOnly && (
          <button
            type="button"
            onClick={() => { setShowCreateForm((v) => !v); setFormError(''); setForm(EMPTY_FORM); }}
            className={btnPrimary}
          >
            {showCreateForm ? 'Cancel' : '+ Add Quota'}
          </button>
        )}
      </div>

      {showCreateForm && (
        <form
          onSubmit={handleCreate}
          className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3"
        >
          <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">New Margin Quota</p>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className={labelClass}>
              Margin Threshold % <span className="text-[rgb(var(--color-danger))]">*</span>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={form.margin_threshold_pct}
                onChange={(e) => setForm((f) => ({ ...f, margin_threshold_pct: e.target.value }))}
                placeholder="0.00"
                className={inputClass}
                required
              />
            </label>

            <label className={labelClass}>
              Payout Rate % <span className="text-[rgb(var(--color-danger))]">*</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.payout_rate_pct}
                onChange={(e) => setForm((f) => ({ ...f, payout_rate_pct: e.target.value }))}
                placeholder="0.00"
                className={inputClass}
                required
              />
            </label>

            <label className={labelClass}>
              Effective Date
              <input
                type="date"
                value={form.effective_date}
                onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))}
                className={inputClass}
              />
            </label>

            <label className={labelClass}>
              Expiration Date
              <input
                type="date"
                value={form.expiration_date}
                onChange={(e) => setForm((f) => ({ ...f, expiration_date: e.target.value }))}
                className={inputClass}
              />
            </label>

            <label className={labelClass + ' sm:col-span-2'}>
              Notes
              <textarea
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                rows={2}
                placeholder="Optional notes"
                className={inputClass}
              />
            </label>
          </div>

          {formError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
              {formError}
            </div>
          )}

          <div className="flex gap-2">
            <button type="submit" disabled={saving} className={btnPrimary}>
              {saving ? 'Saving…' : 'Create Quota'}
            </button>
            <button
              type="button"
              onClick={() => { setShowCreateForm(false); setFormError(''); setForm(EMPTY_FORM); }}
              className={btnSecondary}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {listLoading && (
        <div className="animate-pulse space-y-2">
          <div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" />
          <div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" />
        </div>
      )}

      {listError && (
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {listError}
        </div>
      )}

      {!listLoading && !listError && items.length === 0 && (
        <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-8 text-center">
          <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">No margin quotas configured</p>
          <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Add a margin quota to configure margin-based compensation for this component.
          </p>
        </div>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
          <table className="min-w-full border-collapse">
            <thead>
              <tr>
                <th className={thClass}>Margin Threshold</th>
                <th className={thClass}>Payout Rate</th>
                <th className={thClass}>Effective</th>
                <th className={thClass}>Expires</th>
                <th className={thClass}>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.margin_quota_id}
                  className="cursor-pointer hover:bg-[rgb(var(--color-surface-muted))] transition-colors"
                  onClick={() => { setSelectedItem(item); setActionError(''); }}
                >
                  <td className={tdClass + ' font-medium'}>
                    {item.margin_threshold_pct != null ? `${item.margin_threshold_pct}%` : '—'}
                  </td>
                  <td className={tdClass}>
                    {item.payout_rate_pct != null ? `${item.payout_rate_pct}%` : '—'}
                  </td>
                  <td className={tdClass}>{item.effective_date ?? '—'}</td>
                  <td className={tdClass}>{item.expiration_date ?? '—'}</td>
                  <td className={tdClass}>
                    <StatusPill status={deriveState(item)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {toast && (
        <div
          role="status"
          className="fixed bottom-4 right-4 z-50 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text))] shadow-lg"
        >
          {toast}
        </div>
      )}
    </section>
  );
}
